import { createDraftSafeSelector } from "@reduxjs/toolkit";

import {
  HighchartsChartDataSeriesOption,
  HighchartsChartDataType,
} from "@/storybook/c8r-highcharts/common/utils/types/common";
import { budgetSeriesName } from "@/components/pages/common/billing-explorer/utils/addBudgetLineIntoBillingExplorerChartData";
import { budgetChartSummarizedWithBudgetDataSelector } from "@/store/budgets/selectors/current-budget/chart/budgetChartSummarizedWithBudgetDataSelector";
import { currentBudgetThresholdsSelector } from "@/store/budgets/selectors/current-budget/data-selectors/currentBudgetThresholdsSelector";
import { getNumberByPercentage } from "@/utils/numeral/getPercentage";
import { highChartColors } from "@/storybook/c8r-highcharts/common/utils/constants/chartColors";

export const budgetHighChartDataSelector = createDraftSafeSelector(
  [
    budgetChartSummarizedWithBudgetDataSelector,
    currentBudgetThresholdsSelector,
  ],
  (totals, threshold): HighchartsChartDataType | null => {
    if (!totals) {
      return null;
    }

    const categories = totals?.map(({ date }) => date);

    const actualSeries: HighchartsChartDataSeriesOption = {
      name: "Actual",
      color: "#72BEF4",
      type: "column",
      data:
        totals?.map(({ total }) => ({
          y: total ?? 0,
        })) ?? [],
    };

    const thresholdSeries: HighchartsChartDataSeriesOption[] =
      threshold
        ?.filter(({ percent }) => !!percent)
        .map(({ percent }, index) => {
          return {
            name: `${percent.toFixed(2)}% threshold`,
            type: "line",
            color: highChartColors.at(index + 1),
            dashStyle: "Dash",
            step: "center",
            data: totals.map(({ budget }) => ({
              y: getNumberByPercentage(percent, budget) ?? 0,
            })),
            marker: { enabled: false },
            lineWidth: 2,
            purpose: "visualization",
          };
        }) ?? [];

    const budgetSeries: HighchartsChartDataSeriesOption = {
      name: budgetSeriesName,
      type: "line",
      color: "red",
      step: "center",
      data: totals.map(({ budget }) => ({ y: budget ?? 0 })),
      marker: { enabled: false },
      lineWidth: 2,
      purpose: "visualization",
    };

    return {
      categories,
      series: [actualSeries, budgetSeries, ...thresholdSeries],
    };
  },
);
